import { NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { AuthService } from '@chefami-monorepo/data-access';
import { UserData } from '@chefami-monorepo/types';

type MenuLink = {
  label: string;
  path?: string;
  action?: () => void;
};

type MenuItem = {
  id: string;
  label: string;
  path?: string;
  submenu?: MenuLink[];
};

@Component({
  selector: 'app-header',
  imports: [NgOptimizedImage, RouterLink, RouterLinkActive],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'onEscape()',
  },
})
export class HeaderComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private closeMenuTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly mobileMenuDialog =
    viewChild<ElementRef<HTMLDialogElement>>('mobileMenuDialog');
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private shouldFocusMainOnNextNavigation = false;

  constructor() {
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (
          this.shouldFocusMainOnNextNavigation &&
          event instanceof NavigationEnd
        ) {
          this.shouldFocusMainOnNextNavigation = false;
          this.focusMainContent();
        }
      });

    this.destroyRef.onDestroy(() => {
      this.clearCloseMenuTimeout();
    });
  }

  options = input.required<UserData>();

  readonly userFirstName = computed(
    () => this.options().userInfo?.first_name?.trim() || '',
  );

  readonly welcomeMessage = computed(() => `Welcome, ${this.userFirstName()}`);

  siteInfo = computed(() => this.options().siteInfo);
  locationName = computed(
    () => this.siteInfo()?.location_name || 'Gainesville',
  );
  locationUrl = computed(() => this.siteInfo()?.location_url || '/gainesville');
  logoImageUrl = computed(
    () => `${this.siteInfo()?.site_url || '/'}img/logo@2x.png`,
  );

  desktopOpenMenuId = signal<string | null>(null);
  isMobileMenuOpen = signal(false);
  // Mobile dialog shows all links together (flat list).
  // Keep submenu state commented for quick restore if accordion submenu returns.
  // mobileExpandedMenus = signal<Record<string, boolean>>({});

  primaryMenus = computed<MenuItem[]>(() => {
    const userInfo = this.options().userInfo;
    const availableFreeboxes = userInfo?.available_freeboxes ?? 0;
    const shouldShowFreeBoxes =
      availableFreeboxes > 0 &&
      userInfo?.block_freebox !== 1 &&
      userInfo?.subscription_status !== 3;

    const menuItems: MenuItem[] = [
      {
        id: 'delivery-schedule',
        label: 'Upcoming Deliveries',
        path: '/delivery-schedule',
      },
      {
        id: 'referral',
        label: 'Refer A Friend',
        path: '/referral',
      },
    ];

    if (shouldShowFreeBoxes) {
      menuItems.push({
        id: 'free-boxes',
        label: `Free Meals (${availableFreeboxes})`,
        path: '/free-boxes',
      });
    }

    menuItems.push({
      id: 'donations',
      label: 'Your Donations',
      path: '/donations',
    });

    return menuItems;
  });

  readonly accountMenu = computed<MenuItem>(() => {
    return {
      id: 'my-account',
      label: this.welcomeMessage(),
      submenu: [
        { label: 'Past Deliveries', path: '/my-orders' },
        { label: 'Recipe Ratings', path: '/my-ratings' },
        { label: 'Credits & Gift Cards', path: '/wallets' },
        { label: 'Account Settings', path: '/profile' },
        {
          label: 'Logout',
          action: () => {
            this.auth.logout();
          },
        },
      ],
    };
  });

  openDesktopMenu(menuId: string): void {
    this.desktopOpenMenuId.set(menuId);
  }

  toggleDesktopMenu(menuId: string): void {
    this.desktopOpenMenuId.update((currentMenuId) =>
      currentMenuId === menuId ? null : menuId,
    );
  }

  closeDesktopMenus(): void {
    this.desktopOpenMenuId.set(null);
  }

  closeAllMenus(): void {
    this.closeDesktopMenus();
    this.closeMobileMenu();
  }

  onEscape(): void {
    const openMenuId = this.desktopOpenMenuId();
    const host: HTMLElement = this.hostElement.nativeElement;
    const trigger = openMenuId
      ? host.querySelector<HTMLElement>(
          `[aria-controls="submenu-${openMenuId}"]`,
        )
      : null;
    const focusWasInMenu = !!trigger?.parentElement?.contains(
      document.activeElement,
    );

    this.closeAllMenus();

    // Return focus to the dropdown's trigger instead of leaving it on a
    // now-hidden link.
    if (focusWasInMenu) {
      trigger?.focus();
    }
  }

  onDesktopMenuFocusOut(event: FocusEvent): void {
    const menu = event.currentTarget as HTMLElement;
    const nextFocused = event.relatedTarget as Node | null;

    // A null relatedTarget is a click on non-focusable content (or a Safari
    // link click); outside clicks are handled by onDocumentClick.
    if (nextFocused && !menu.contains(nextFocused)) {
      this.closeDesktopMenus();
    }
  }

  onMenuNavigation(): void {
    this.shouldFocusMainOnNextNavigation = true;
    this.closeAllMenus();
  }

  isDesktopMenuExpanded(menuId: string): boolean {
    return this.desktopOpenMenuId() === menuId;
  }

  toggleMobileMenu(): void {
    if (this.isMobileMenuOpen()) {
      this.closeMobileMenu();
      return;
    }

    this.openMobileMenu();
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen.set(false);
    // this.mobileExpandedMenus.set({});

    this.clearCloseMenuTimeout();

    const dialog = this.mobileMenuDialog()?.nativeElement;
    if (!dialog?.open) {
      return;
    }

    this.closeMenuTimeoutId = setTimeout(() => {
      dialog.close();
      this.closeMenuTimeoutId = null;
    }, 500);
  }

  onMobileDialogCancel(event: Event): void {
    event.preventDefault();
    this.closeMobileMenu();
  }

  onMobileDialogClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closeMobileMenu();
    }
  }

  onMobileDialogKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      this.trapMobileDialogFocus(event);
    }
  }

  onMobileDialogClose(): void {
    this.isMobileMenuOpen.set(false);
    // this.mobileExpandedMenus.set({});
  }

  /*
  isMobileSubmenuExpanded(menuId: string): boolean {
    return !!this.mobileExpandedMenus()[menuId];
  }

  toggleMobileSubmenu(menuId: string): void {
    this.mobileExpandedMenus.update((expandedMenus) => ({
      ...expandedMenus,
      [menuId]: !expandedMenus[menuId],
    }));
  }
  */

  onDocumentClick(event: Event): void {
    const target = event.target as Node | null;
    if (!target || this.hostElement.nativeElement.contains(target)) {
      return;
    }

    this.closeDesktopMenus();
  }

  private openMobileMenu(): void {
    const dialog = this.mobileMenuDialog()?.nativeElement;
    if (!dialog) {
      return;
    }

    this.clearCloseMenuTimeout();

    if (!dialog.open) {
      dialog.showModal();
    }

    requestAnimationFrame(() => {
      this.isMobileMenuOpen.set(true);
      this.focusFirstMobileDialogElement();
    });
  }

  private focusFirstMobileDialogElement(): void {
    const dialog = this.mobileMenuDialog()?.nativeElement;
    if (!dialog) {
      return;
    }

    const focusableElements = this.getMobileDialogFocusableElements(dialog);
    if (!focusableElements.length) {
      dialog.focus();
      return;
    }

    focusableElements[0].focus();
  }

  private trapMobileDialogFocus(event: KeyboardEvent): void {
    const dialog = this.mobileMenuDialog()?.nativeElement;
    if (!dialog) {
      return;
    }

    const focusableElements = this.getMobileDialogFocusableElements(dialog);
    if (!focusableElements.length) {
      event.preventDefault();
      dialog.focus();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement as HTMLElement | null;

    if (event.shiftKey && activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
      return;
    }

    if (!event.shiftKey && activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  private getMobileDialogFocusableElements(
    dialog: HTMLDialogElement,
  ): HTMLElement[] {
    return Array.from(
      dialog.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    );
  }

  private clearCloseMenuTimeout(): void {
    if (!this.closeMenuTimeoutId) {
      return;
    }

    clearTimeout(this.closeMenuTimeoutId);
    this.closeMenuTimeoutId = null;
  }

  private focusMainContent(): void {
    requestAnimationFrame(() => {
      const mainElement = document.getElementById('main');
      if (!mainElement) {
        return;
      }

      if (!mainElement.hasAttribute('tabindex')) {
        mainElement.setAttribute('tabindex', '-1');
      }

      mainElement.focus();
    });
  }
}
